import {
  Body,
  Box,
  Flex,
  Heading,
  IconButton,
  useBreakpointValue,
} from '@hover/blueprint';
import { iconClose } from '@hover/icons';
import React from 'react';

import type { DismissableCardSizes } from 'src/modules/homeownerApp/components/Card/dismissableCardHelpers';
import {
  buttonAlignment,
  buttonContainerOrientation,
  cardOrientation,
  cardTextAlignment,
  secondaryButtonVariant,
} from 'src/modules/homeownerApp/components/Card/dismissableCardHelpers';
import { Button } from 'src/shared/components';

import { BasicCard } from './BasicCard';
import type { SplitCardProps } from './SplitCard';

interface DismissableCardProps
  extends Omit<SplitCardProps, 'onResponsiveChangeOrientation' | 'children'> {
  eyebrow?: string;
  headline: string;
  subCopy?: string;
  onClose?: () => void;
  primaryCtaText?: string;
  secondaryCtaText?: string;
  onPrimaryCtaClick?: () => void;
  onSecondaryCtaClick?: () => void;
  isDismissable?: boolean;
  size?: DismissableCardSizes;
}

export const DismissableCard = ({
  eyebrow,
  headline,
  subCopy,
  imageBoxProps,
  backgroundImage,
  descriptionBoxProps,
  onClose,
  primaryCtaText,
  secondaryCtaText,
  onPrimaryCtaClick,
  onSecondaryCtaClick,
  isDismissable = true,
  size,
  ...props
}: DismissableCardProps) => {
  const breakPointCardSize = useBreakpointValue<DismissableCardSizes>({
    base: 'md',
    desktop: 'lg',
  });

  const cardSize = size ?? breakPointCardSize;

  return (
    <BasicCard
      flexDirection={cardOrientation(cardSize)}
      position="relative"
      {...props}
    >
      <Flex flex={1} flexDirection="column" {...descriptionBoxProps}>
        <Flex direction="column" gap={500} px={500} py={600}>
          <Flex direction="column" textAlign={cardTextAlignment(cardSize)}>
            <Heading mb="100" textStyle="headingXs">
              {eyebrow}
            </Heading>
            <Heading mb="300" textStyle="headingMedium">
              {headline}
            </Heading>
            <Body color="neutral.500" m="0" textStyle="bodySmall">
              {subCopy}
            </Body>
          </Flex>
          <Flex
            flexDirection={buttonContainerOrientation(cardSize)}
            gap="200"
            justifyContent={buttonAlignment(cardSize)}
          >
            {primaryCtaText && (
              <Button onClick={onPrimaryCtaClick} primaryCallToAction>
                {primaryCtaText}
              </Button>
            )}
            {secondaryCtaText && (
              <Button
                onClick={onSecondaryCtaClick}
                variant={secondaryButtonVariant(cardSize)}
              >
                {secondaryCtaText}
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
      {backgroundImage && (
        <Box
          backgroundColor="neutral.50"
          backgroundSize="contain"
          bgColor="neutral.100"
          bgImage={backgroundImage}
          bgPosition="center"
          bgRepeat="no-repeat"
          bgSize="cover"
          flex={1}
          height="100%"
          sx={{ aspectRatio: { base: '3/2', desktop: 'auto' } }}
          width={{ base: '100%', desktop: 'unset' }}
          {...imageBoxProps}
        />
      )}
      {isDismissable && (
        <IconButton
          icon={iconClose}
          label="close"
          onClick={onClose}
          position="absolute"
          right={300}
          top={300}
          variant="ghost"
        />
      )}
    </BasicCard>
  );
};
